import { enqueueSnackbar } from 'notistack';
import { useCallback } from 'react';
import { CustomTargetObject } from '~/engine/data/common/customs/CustomTargetObject';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { SDFOperations } from '~/engine/util/algorithms/sdf/SDFOperations';
import { Environment } from '~/engine/data/Environment';
import { getErrorMessage } from '~/utils/errors';
import { waitForUI } from '~/utils/uiUtils';
import { getPreviewColor } from '../margin/marginToolbarUtils';
import { usePreviewBase } from '../shared/usePreviewBase';

interface UseRingPreviewOptions {
    environment: Environment | null;
    inputRoi: RegionOfInterest | undefined;
}

export function useRingPreview({
    environment,
    inputRoi,
}: UseRingPreviewOptions) {
    const {
        isPreviewing,
        setIsPreviewing,
        abortRef,
        updateView,
        clearPreview,
        cleanup,
    } = usePreviewBase({ environment });

    const handlePreviewRing = useCallback(
        async (lowerMm: number, upperMm: number) => {
            if (
                !environment?.structureSet ||
                !inputRoi?.texture ||
                !environment.image
            ) {
                return;
            }

            if (upperMm <= lowerMm) {
                clearPreview();
                return;
            }

            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;

            setIsPreviewing(true);

            // Use the largest absolute boundary as the placeholder size so
            // the bounding box covers all cases (ring inside, outside, or
            // spanning the surface).
            const placeholderMm = Math.max(
                Math.abs(lowerMm),
                Math.abs(upperMm)
            );
            const previewTexture = ContourTexture.createNewWithMargin(
                placeholderMm,
                inputRoi.texture
            );
            const previewObj = new CustomTargetObject(
                environment.image,
                previewTexture,
                getPreviewColor(inputRoi.color)
            );
            previewObj.renderOptions = { fillAlpha: 0.18 };
            environment.operationPreviewStructure = previewObj;
            // TODO: improve these kind of hacky rendering solutions should be removed and
            // heavy computation should be lifted away from blocking the main thread for smooth rendering
            await waitForUI();

            try {
                const ringTexture = await SDFOperations.applyRing(
                    inputRoi.texture,
                    lowerMm,
                    upperMm
                );

                // A newer preview started (or cleanup ran) while the ring was
                // computing — discard the stale result to avoid a GPU texture
                // leak and avoid overwriting the current preview object.
                if (controller.signal.aborted) {
                    ringTexture.delete();
                    return;
                }

                // Replace the placeholder texture and delete it to free GPU
                // resources; ContourTexture.contourTexture setter does not
                // dispose the previous value.
                const placeholder = previewObj.contourTexture;
                previewObj.contourTexture = ringTexture;
                placeholder?.delete();

                updateView();
            } catch (e) {
                console.error('Ring preview error:', e);
                clearPreview();
                enqueueSnackbar(`Ring preview failed: ${getErrorMessage(e)}`, {
                    variant: 'error',
                });
            } finally {
                setIsPreviewing(false);
            }
        },
        [
            environment,
            inputRoi,
            abortRef,
            setIsPreviewing,
            clearPreview,
            updateView,
        ]
    );

    return {
        isPreviewing,
        handlePreviewRing,
        clearPreview,
        cleanup,
        updateView,
    };
}
